import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  toast,
} from '@kmgestao/ui';

import { LOST_REASONS, LOST_REASON_LABELS, useMarkLost } from '../../../features/leads/index.js';

export interface MarkAsLostDialogProps {
  leadId: string;
  leadName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMarked?: () => void;
}

/**
 * Dialog do DS (Radix) para Karla arquivar um lead como "perdido".
 * Motivo estruturado (enum → `leads.lost_reason`) + detalhe opcional
 * (`metadata.lost_reason_detail`). O `useMarkLost` também insere uma
 * `lead_activities` do tipo `stage_changed` pra timeline.
 */
export function MarkAsLostDialog({
  leadId,
  leadName,
  open,
  onOpenChange,
  onMarked,
}: MarkAsLostDialogProps) {
  const markLost = useMarkLost();
  const [reason, setReason] = useState<(typeof LOST_REASONS)[number]>('no_response');
  const [detail, setDetail] = useState('');

  const onConfirm = async () => {
    await markLost.mutateAsync(
      {
        lead_id: leadId,
        reason,
        reason_detail: detail.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success(`Lead "${leadName}" marcado como perdido.`);
          onOpenChange(false);
          onMarked?.();
        },
        onError: (err) =>
          toast.error('Falha ao arquivar lead', { description: (err as Error).message }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Marcar como perdido</DialogTitle>
          <DialogDescription>
            Arquivar <strong className="text-foreground">{leadName}</strong>. A mudança fica
            registrada na timeline — não é destrutivo. Motivo ajuda análise agregada de rejeição.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="lost-reason">Motivo</Label>
            <Select
              id="lost-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value as (typeof LOST_REASONS)[number])}
            >
              {LOST_REASONS.map((r) => (
                <option key={r} value={r}>
                  {LOST_REASON_LABELS[r]}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="lost-detail">Detalhe (opcional)</Label>
            <textarea
              id="lost-detail"
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              rows={3}
              maxLength={500}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm"
              placeholder="Contexto adicional — conversa com o lead, concorrente escolhido, etc."
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline" size="lg" disabled={markLost.isPending}>
              Cancelar
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            size="lg"
            onClick={onConfirm}
            disabled={markLost.isPending}
          >
            {markLost.isPending ? 'Arquivando…' : 'Confirmar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
